import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue.ts';
import React, { useContext, useRef } from 'react';
import {
	useCurrentLanguage,
	useTranslationHelpBlog,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Flex, Text, Title } from '@mantine/core';
import { ArticlePreview } from '@dealstack/frontend/app/pages/HelpBlog/ArticlePreview/ArticlePreview.tsx';
import { StringInput as StringInputV2 } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2.tsx';
import { useArticles } from '@dealstack/frontend/app/pages/HelpBlog/api/useArticles.ts';
import { HelpBlogPageContainer } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlogPage/HelpBlogPage.styles.tsx';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useHelpBlogBreadcrumbs } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogBreadcrumbs.tsx';
import { ArticleRes } from '@dealstack/shared/lib/generated';
import { generatePath } from 'react-router-dom';
import { HotkeyButton } from '@dealstack/frontend/shared/components/HotkeyButton/HotkeyButton.tsx';
import { useOS } from '@dealstack/frontend/shared/hooks/useOs/useOs.hook.ts';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer.tsx';
import { HelpBlogContext } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogController.tsx';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';

export enum ARTICLE_SEARCH_FORM_FIELDS {
	QUERY = 'query',
}

export const HelpBlogPage = () => {
	const { dataset, perspective, mediaResolver } = useContext(HelpBlogContext);
	const tHelpBlog = useTranslationHelpBlog();
	const currentLanguage = useCurrentLanguage();
	const os = useOS();
	const prevArticles = useRef<ArticleRes[]>();
	const inputRef = useRef<HTMLInputElement | null>(null);

	useHelpBlogBreadcrumbs([
		{
			name: tHelpBlog('breadcrumbs.help_crumb'),
			url: ROUTES.HELP_BLOG,
		},
		{
			name: tHelpBlog('breadcrumbs.gettingStarted'),
			url: ROUTES.HELP_BLOG,
		},
	]);

	const searchForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
	});
	const queryValue = useWatch({
		control: searchForm.control,
		name: ARTICLE_SEARCH_FORM_FIELDS.QUERY,
	});
	const searchQueryDebounced = useDebounceValue(queryValue, 500);

	const { articles, isFetching } = useArticles(
		dataset,
		{
			enabled: true,
			searchKeyword: searchQueryDebounced,
			language: currentLanguage,
		},
		perspective
	);
	let displayArticles = articles;

	if (isFetching && prevArticles.current) {
		displayArticles = prevArticles.current;
	} else {
		prevArticles.current = articles;
	}

	const handleHotkeyAction = () => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	};

	const SearchHotkeyButton =
		os?.type === 'desktop' ? (
			<HotkeyButton
				osCheck={os}
				macHotkey={{
					combo: 'mod+k',
					text: '⌘ K',
				}}
				otherHotkey={{
					combo: 'ctrl+k',
					text: 'Ctrl K',
				}}
				onClick={handleHotkeyAction}
			/>
		) : undefined;

	return (
		<HelpBlogPageContainer>
			<PageHeader pageTitle={tHelpBlog('page.title')} showQuickSwitch={false}></PageHeader>
			<PageContainer className="page-container">
				<div className="container">
					<div className="header">
						<Title size="h1" fw={600} className="page-title">
							{tHelpBlog('page.innerTitle')}
						</Title>

						<Text fz="lg" className="subtitle">
							{tHelpBlog('page.subtitle')}
						</Text>

						<div className="search">
							<FormProvider {...searchForm}>
								<StringInputV2
									onInputChange={() => null}
									inputPlaceHolder={tHelpBlog('page.search.placeholder')}
									name={ARTICLE_SEARCH_FORM_FIELDS.QUERY}
									size="search"
									leftIcon="search-v3"
									hotkeyComponent={SearchHotkeyButton}
									inputRef={inputRef}
								/>
							</FormProvider>
						</div>

						<Flex gap="0.75rem" justify="center" align="baseline">
							<Text fz="sm">{tHelpBlog('callout.title')}</Text>

							<Text fz="m" fw={600}>
								<a
									href={`mailto:${tHelpBlog('contact.email')}`}
									className="contact-link"
								>
									{tHelpBlog('contact.email')}
								</a>
							</Text>
						</Flex>
					</div>

					<Flex wrap="wrap" gap="md" className="articles">
						{displayArticles?.map((article) => {
							return (
								<div
									key={article.id}
									className={`col ${isFetching ? 'loading' : ''}`}
								>
									<ArticlePreview
										title={article.title}
										slug={article.slug.current}
										richText={article.previewSummary}
										author={article.author.name}
										authorSlug={article.author.slug.current}
										publishedAt={article.createdAt}
										categoryName={article.category.name}
										categoryUrl={generatePath(HELP_BLOG_ROUTES.CATEGORY, {
											slug: article.category.slug.current,
										})}
										mediaResolver={mediaResolver}
									/>
								</div>
							);
						})}
					</Flex>
				</div>
			</PageContainer>
		</HelpBlogPageContainer>
	);
};
